import { animate } from "motion"
import { identify, track } from "./analytics.js"

/*
 * "Your spot": after joining the waitlist, where you are in the queue, what
 * you get, and the ways to share your link (src/_partials/_spot.erb). The
 * queue itself is worker/index.js.
 *
 * Remembered on this device: your own code (to show your spot again) and a
 * friend's code from their link (so your signup moves them up), both in
 * localStorage, both optional: everything works without them.
 */

const OWN = "bo:spot"
const FRIEND = "bo:w"
const EMAIL = "bo:spot-email"
const CODE = /^[A-Z2-9]{6}$/

const store = {
  get(k) {
    try {
      return localStorage.getItem(k)
    } catch {
      return null
    }
  },
  set(k, v) {
    try {
      v == null ? localStorage.removeItem(k) : localStorage.setItem(k, v)
    } catch {
      /* private mode: fine */
    }
  },
}

const motionOK = () => document.documentElement.classList.contains("motion")
const dialog = document.querySelector("[data-spot]")
// The card is the popup's, or the /spot/CODE page's when it's moved there.
const card = dialog?.querySelector("[data-spot-card]")
const $ = (sel) => card?.querySelector(sel)
let current = null

/** The friend's code, if this visit came from their link, and it isn't our own. */
export function friendCode() {
  const fromUrl = (new URLSearchParams(location.search).get("w") || "").toUpperCase()
  if (CODE.test(fromUrl) && fromUrl !== store.get(OWN)) store.set(FRIEND, fromUrl)
  const code = store.get(FRIEND)
  return code && code !== store.get(OWN) ? code : undefined
}

const shareText = () =>
  `I just joined the waitlist for Baby OOTD, an app that tells you what your baby should wear (sleep sacks and TOG too). The first 50 get Plus free. Join with my link and I move up the list:`

const prettyUrl = (url) => url.replace(/^https:\/\/(www\.)?/, "")

/** f…@gmail.com: enough to recognise, not enough to read over a shoulder. */
const mask = (email) => {
  const [user, domain] = String(email || "").split("@")
  return user && domain ? `${user[0]}…@${domain}` : ""
}

const someone = (place) => (place ? `A parent in ${place}` : "A parent")

/** The anonymous rows around your spot, with a dashed line wherever a reward tier ends. */
function fillAround(s) {
  const box = $("[data-spot-around]")
  const rows = s.around ?? []
  box.hidden = rows.length < 2
  if (box.hidden) return
  const list = $("[data-spot-around-list]")
  list.replaceChildren()
  rows.forEach((r, i) => {
    const li = document.createElement("li")
    li.className = `spot-row${r.you ? " is-you" : ""}`
    const num = document.createElement("b")
    num.textContent = `#${r.position}`
    const dot = document.createElement("span")
    dot.className = "dot"
    dot.style.background = r.you ? "#ffd27a" : `hsl(${r.hue} 70% 78%)`
    const who = document.createElement("span")
    who.className = "who"
    who.textContent = r.you ? "You" : someone(r.place)
    const note = document.createElement("span")
    note.className = "note"
    note.textContent = r.friends ? `+${r.friends} ${r.friends === 1 ? "friend" : "friends"}` : !r.you && Date.now() - r.joined < 86_400_000 ? "new today" : ""
    li.append(num, dot, who, note)
    list.append(li)
    // A tier ends between this row and the next.
    const after = rows[i + 1]
    const tier = after && (s.tiers ?? []).find((t) => r.position <= t.top && after.position > t.top)
    if (tier) {
      const line = document.createElement("li")
      line.className = "spot-line"
      line.setAttribute("aria-label", `${tier.name} ends here`)
      line.textContent = `${tier.name} · ${tier.reward}`
      list.append(line)
    }
  })
}

function fill(s, { fresh = false } = {}) {
  current = s
  const tier = s.tier
  $("[data-spot-eyebrow]").textContent = fresh ? "Thanks for joining! 🎉" : "Your spot on the waitlist"
  $("[data-spot-position]").textContent = s.position
  // Joining early is the point, so say it that way, not "#7 of 7".
  const total = s.total === 1 ? "You're the first parent on the list." : `${s.total} parents on the list so far.`
  $("[data-spot-total]").innerHTML = tier
    ? `${total} Your spot gets <b>${tier.reward}</b>.`
    : `${total} The top 50 get Plus free.`

  const next = $("[data-spot-next]")
  if (s.nextTier) {
    const n = s.nextTier.friends
    const target = Number(s.nextTier.name.replace(/\D/g, ""))
    $("[data-spot-next-text]").textContent = `${n} ${n === 1 ? "friend" : "friends"} away from the ${s.nextTier.name.toLowerCase()}`
    $("[data-spot-next-sub]").textContent = `That's ${s.nextTier.reward}. Share your link:`
    $("[data-spot-from]").textContent = `#${s.position}`
    $("[data-spot-to]").textContent = `#${target}`
    $("[data-spot-bar-row]").hidden = false
    const bar = $("[data-spot-bar]")
    const pct = `${Math.max(8, Math.min(100, (target / s.position) * 100))}%`
    if (motionOK()) animate(bar, { width: ["0%", pct] }, { type: "spring", bounce: 0, duration: 0.9, delay: 0.25 })
    else bar.style.width = pct
  } else {
    // Already in the top 5: keep them sharing for the fun of it, and to stay there.
    $("[data-spot-next-text]").textContent = "You're in the top 5 🏆"
    $("[data-spot-next-sub]").textContent = "Others are sharing too. Keep your spot: share your link."
    $("[data-spot-bar-row]").hidden = true
  }
  // With people around them, the list says it all: its heading is the progress line.
  fillAround(s)
  next.hidden = !$("[data-spot-around]").hidden
  $("[data-spot-around-title]").textContent = $("[data-spot-next-text]").textContent

  card.querySelectorAll("[data-top]").forEach((li) => li.classList.toggle("is-mine", tier?.top === Number(li.dataset.top)))
  $("[data-spot-url]").textContent = prettyUrl(s.url)
  $("[data-spot-friends]").textContent = s.referrals ? ` ${s.referrals} ${s.referrals === 1 ? "friend has" : "friends have"} joined with yours.` : ""

  const text = shareText()
  $("[data-spot-whatsapp]").href = `https://wa.me/?text=${encodeURIComponent(`${text} ${s.url}`)}`
  $("[data-spot-facebook]").href = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(s.url)}`
  $("[data-spot-email]").href =
    `mailto:?subject=${encodeURIComponent("A free month of Baby OOTD Plus?")}&body=${encodeURIComponent(
      `Hi!\n\nI joined the waitlist for Baby OOTD, an app that tells you what your baby should wear: it checks the weather and the room, and helps with sleep sacks and TOG.\n\nThe first 50 on the list get Plus free, and if you join with my link I move up:\n${s.url}\n\nx`,
    )}`
  // Texting is for phones; "&body" works on both iOS and Android.
  const sms = $("[data-spot-sms]")
  sms.hidden = !matchMedia("(pointer: coarse)").matches
  sms.href = `sms:?&body=${encodeURIComponent(`${text} ${s.url}`)}`
  $("[data-spot-native]").hidden = !navigator.share
  // On phones, "More" opens the share sheet, which has Facebook too: one less button.
  $("[data-spot-facebook]").hidden = !!navigator.share && matchMedia("(pointer: coarse)").matches

  const email = s.email ? mask(s.email) : store.get(EMAIL)
  if (s.email) store.set(EMAIL, email)
  const reassure = $("[data-spot-reassure]")
  reassure.textContent = email ? `You'll get an email at ${email} as soon as the app launches.` : "You'll get an email as soon as the app launches."
  say("")

  // Everywhere else on the page: the nav button and finished forms point here.
  document.querySelectorAll("[data-nav-join]").forEach((a) => (a.textContent = `You're #${s.position}`))
  document.querySelectorAll("[data-done-spot]").forEach((el) => (el.textContent = `, at #${s.position}`))
  document.querySelectorAll("[data-waitlist]").forEach((f) => f.classList.add("is-done"))
}

/** A small burst of confetti from the number, once, when they've just joined. */
function confetti() {
  if (!motionOK()) return
  const card = $("[data-spot-card]")
  const origin = $("#spot-title").getBoundingClientRect()
  const box = card.getBoundingClientRect()
  const colours = ["#ec6a3c", "#ffd27a", "#ff8fa3", "#8a6cf0", "#2a73c4"]
  for (let i = 0; i < 36; i++) {
    const bit = document.createElement("i")
    bit.className = "spot-bit"
    bit.style.background = colours[i % colours.length]
    bit.style.left = `${origin.left - box.left + 150}px`
    bit.style.top = `${origin.top - box.top + 30}px`
    card.append(bit)
    const angle = (Math.PI * 2 * i) / 36 + Math.random() * 0.4
    const dist = 90 + Math.random() * 130
    animate(
      bit,
      {
        x: [0, Math.cos(angle) * dist],
        y: [0, Math.sin(angle) * dist * 0.7 + 120],
        rotate: [0, (Math.random() - 0.5) * 720],
        opacity: [1, 1, 0],
      },
      { duration: 1.3 + Math.random() * 0.5, ease: [0.2, 0.7, 0.4, 1], delay: 0.25 },
    ).then(() => bit.remove())
  }
}

const say = (text) => {
  const msg = $("[data-spot-msg]")
  if (msg) msg.textContent = text
}

/** Shows the spot. With `s` (straight from joining), it's news: the eyebrow says so and there's confetti. */
export function openSpot(s) {
  if (!dialog) return
  // On /spot/CODE the card is already the page.
  if (card.parentElement !== dialog) return card.scrollIntoView({ behavior: motionOK() ? "smooth" : "auto", block: "start" })
  if (s) {
    store.set(OWN, s.code)
    store.set(FRIEND, null)
    fill(s, { fresh: true })
  }
  if (!current || dialog.open) return
  dialog.showModal()
  track("spot_opened", { fresh: !!s, position: current.position, tier: current.tier?.name ?? null })
  if (motionOK()) {
    animate($("[data-spot-card]"), { opacity: [0, 1], y: [48, 0] }, { type: "spring", bounce: 0, duration: 0.5 })
    // It's good news: the number lands with a little bounce.
    animate($("#spot-title"), { scale: [0.7, 1], opacity: [0, 1] }, { type: "spring", bounce: 0.35, duration: 0.6, delay: 0.12 })
    if (s) confetti()
  }
}

async function closeSpot() {
  if (!dialog?.open) return
  if (motionOK()) await animate($("[data-spot-card]"), { opacity: 0, y: 40 }, { type: "spring", bounce: 0, duration: 0.3 })
  dialog.close()
  closeStory()
  $("[data-spot-card]").style.cssText = ""
}

// ── The story image: 1080×1920, inside Instagram's safe area, drawn here ──

const HEART = "M50 88 C20 66 2 50 2 28 C2 13 14 2 28 2 C38 2 46 8 50 16 C54 8 62 2 72 2 C86 2 98 13 98 28 C98 50 80 66 50 88 Z"
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", "Segoe UI", Roboto, sans-serif'

function wrap(ctx, text, x, y, width, lineHeight) {
  const words = text.split(" ")
  let line = ""
  for (const w of words) {
    const test = line ? `${line} ${w}` : w
    if (ctx.measureText(test).width > width && line) {
      ctx.fillText(line, x, y)
      line = w
      y += lineHeight
    } else line = test
  }
  ctx.fillText(line, x, y)
  return y + lineHeight
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.roundRect(x, y, w, h, r)
}

/** The bedtime baby (hidden in the spot partial on every page), as an image to draw. */
async function babyDrawing() {
  const svg = document.querySelector("[data-story-baby] svg") || document.querySelector('[data-phone-state="sleep"] svg')
  if (!svg) return null
  const copy = svg.cloneNode(true)
  copy.setAttribute("xmlns", "http://www.w3.org/2000/svg")
  copy.setAttribute("width", "400")
  copy.setAttribute("height", "500")
  const img = new Image()
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(copy))}`
  try {
    await img.decode()
    return img
  } catch {
    return null
  }
}

/*
 * 1080×1920. Instagram covers the top ~250px and bottom ~340px with its own
 * buttons, so everything that matters sits between. The baby does the talking,
 * like in the app; the link card is big enough to read, and there's room under
 * it for Instagram's Link sticker.
 */
async function storyImage(s) {
  const c = document.createElement("canvas")
  c.width = 1080
  c.height = 1920
  const ctx = c.getContext("2d")
  const sky = ctx.createLinearGradient(0, 0, 0, 1920)
  sky.addColorStop(0, "#ffe9cf")
  sky.addColorStop(0.45, "#ffd9d2")
  sky.addColorStop(1, "#f6e7ff")
  ctx.fillStyle = sky
  ctx.fillRect(0, 0, 1080, 1920)
  const glow = ctx.createRadialGradient(540, 760, 40, 540, 760, 460)
  glow.addColorStop(0, "rgba(255,255,255,0.85)")
  glow.addColorStop(1, "rgba(255,255,255,0)")
  ctx.fillStyle = glow
  ctx.fillRect(0, 200, 1080, 1100)
  ctx.textAlign = "center"

  // Wordmark: a small heart and the name, centred together.
  ctx.font = `800 46px ${FONT}`
  const nameW = ctx.measureText("Baby OOTD").width
  const markX = 540 - (50 + 14 + nameW) / 2
  ctx.save()
  ctx.translate(markX, 262)
  ctx.scale(0.5, 0.5)
  const hg = ctx.createLinearGradient(0, 0, 0, 90)
  hg.addColorStop(0, "#FF8FA3")
  hg.addColorStop(1, "#FF4F6D")
  ctx.fillStyle = hg
  ctx.fill(new Path2D(HEART))
  ctx.restore()
  ctx.fillStyle = "#2e2640"
  ctx.textAlign = "left"
  ctx.fillText("Baby OOTD", markX + 64, 300)
  ctx.textAlign = "center"

  // The baby says it.
  const bubbleTop = 380
  ctx.font = `700 46px ${FONT}`
  const lines = []
  let line = ""
  for (const w of "Mommy, I should wear long sleeves and my 1 TOG sack tonight.".split(" ")) {
    const test = line ? `${line} ${w}` : w
    if (ctx.measureText(test).width > 760 && line) {
      lines.push(line)
      line = w
    } else line = test
  }
  lines.push(line)
  const bubbleH = 70 + lines.length * 60
  ctx.shadowColor = "rgba(46,38,64,0.16)"
  ctx.shadowBlur = 40
  ctx.shadowOffsetY = 16
  ctx.fillStyle = "#ffffff"
  roundRect(ctx, 120, bubbleTop, 840, bubbleH, 52)
  ctx.fill()
  ctx.beginPath()
  ctx.moveTo(510, bubbleTop + bubbleH - 2)
  ctx.lineTo(540, bubbleTop + bubbleH + 34)
  ctx.lineTo(570, bubbleTop + bubbleH - 2)
  ctx.fill()
  ctx.shadowColor = "transparent"
  ctx.fillStyle = "#2e2640"
  lines.forEach((l, i) => ctx.fillText(l, 540, bubbleTop + 78 + i * 60))

  const baby = await babyDrawing()
  const babyTop = bubbleTop + bubbleH + 50
  if (baby) ctx.drawImage(baby, 540 - 170, babyTop, 340, 425)
  else {
    ctx.save()
    ctx.translate(540 - 120, babyTop + 80)
    ctx.scale(2.4, 2.4)
    ctx.fillStyle = hg
    ctx.fill(new Path2D(HEART))
    ctx.restore()
  }

  // The line.
  let y = babyTop + 500
  ctx.fillStyle = "#2e2640"
  ctx.font = `800 76px ${FONT}`
  ctx.fillText("What should your baby wear?", 540, y, 980)
  ctx.fillStyle = "#ec6a3c"
  ctx.fillText("Ask them.", 540, y + 86)
  y += 130

  // The card with the link.
  const top = y + 40
  ctx.shadowColor = "rgba(46,38,64,0.18)"
  ctx.shadowBlur = 50
  ctx.shadowOffsetY = 20
  ctx.fillStyle = "#2e2640"
  roundRect(ctx, 90, top, 900, 270, 56)
  ctx.fill()
  ctx.shadowColor = "transparent"
  ctx.fillStyle = "#fff8f2"
  ctx.font = `800 54px ${FONT}`
  ctx.fillText("Join me on the waitlist 👇", 540, top + 92)
  ctx.fillStyle = "#ffd27a"
  ctx.font = `800 56px ${FONT}`
  ctx.fillText(prettyUrl(s.url), 540, top + 170, 820)
  ctx.fillStyle = "#c8cbe8"
  ctx.font = `600 36px ${FONT}`
  ctx.fillText(`I'm #${s.position} · the first 50 get Plus free`, 540, top + 228, 820)

  return new Promise((resolve) => c.toBlob(resolve, "image/png"))
}

// A website can't open Instagram's story camera; the share sheet (or a download) is the way
// in. So the Story button opens a panel first: the image, three steps, then "Share image",
// a fresh tap that the share sheet needs anyway.
let storyFile = null

async function openStory() {
  if (!current) return
  const panel = $("[data-story-panel]")
  const go = $("[data-story-go]")
  panel.hidden = false
  panel.scrollTop = 0
  if (motionOK()) animate(panel, { opacity: [0, 1], x: [24, 0] }, { type: "spring", bounce: 0, duration: 0.35 })
  // Copy the link now, while the tap counts: Instagram's Link sticker wants it.
  navigator.clipboard?.writeText(current.url).catch(() => {})
  go.disabled = true
  go.textContent = "Making your image…"
  const blob = await storyImage(current)
  storyFile = new File([blob], "baby-ootd-waitlist.png", { type: "image/png" })
  const img = $("[data-story-img]")
  if (img.src) URL.revokeObjectURL(img.src)
  img.src = URL.createObjectURL(blob)
  const sheet = !!navigator.canShare?.({ files: [storyFile] })
  $("[data-story-step1]").textContent = sheet ? "Tap Share image" : "Download the image and send it to your phone"
  $("[data-story-step2]").innerHTML = sheet ? "Pick <b>Instagram</b>, then <b>Story</b>" : "In Instagram, add it to your <b>Story</b>"
  go.textContent = sheet ? "Share image" : "Download image"
  go.disabled = false
}

function closeStory() {
  $("[data-story-panel]").hidden = true
}

async function shareStory() {
  if (!storyFile) return
  try {
    if (navigator.canShare?.({ files: [storyFile] })) {
      await navigator.share({ files: [storyFile] })
      closeStory()
      say("Nice! Don't forget the Link sticker: your link is copied.")
      return
    }
  } catch (e) {
    if (e?.name === "AbortError") return
  }
  const a = document.createElement("a")
  a.href = URL.createObjectURL(storyFile)
  a.download = storyFile.name
  document.body.append(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 4000)
  say("Saved. Post it as a story and add a Link sticker: your link is copied.")
}

// ── Wiring ──

if (dialog) {
  $("[data-spot-close]").addEventListener("click", closeSpot)
  // Esc, and a tap on the dimmed backdrop, close it the same animated way.
  dialog.addEventListener("cancel", (e) => {
    e.preventDefault()
    closeSpot()
  })
  dialog.addEventListener("click", (e) => {
    if (e.target === dialog) closeSpot()
  })
  const copy = $("[data-spot-copy]")
  copy.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(current.url)
      copy.textContent = "Copied ✓"
      setTimeout(() => (copy.textContent = "Copy"), 1800)
    } catch {
      say(current.url)
    }
  })
  // Which way people share: the links open the app themselves, so count the tap.
  for (const [sel, channel] of [["[data-spot-whatsapp]", "whatsapp"], ["[data-spot-facebook]", "facebook"], ["[data-spot-email]", "email"], ["[data-spot-sms]", "text"], ["[data-spot-story]", "instagram_story"], ["[data-spot-native]", "more"], ["[data-spot-copy]", "copy_link"]]) {
    $(sel).addEventListener("click", () => track("share_clicked", { channel, position: current?.position }))
  }
  $("[data-spot-story]").addEventListener("click", () =>
    openStory().catch(() => {
      closeStory()
      say("Couldn't make the image. Try again.")
    }),
  )
  $("[data-story-go]").addEventListener("click", () => shareStory())
  $("[data-story-back]").addEventListener("click", closeStory)
  $("[data-spot-native]").addEventListener("click", () =>
    navigator.share?.({ title: "Baby OOTD", text: shareText(), url: current.url }).catch(() => {}),
  )
  document.querySelectorAll("[data-spot-open]").forEach((b) => b.addEventListener("click", () => openSpot()))

  // Joined before, on this device: show where they are now (it changes as people share).
  const own = store.get(OWN)
  if (own && CODE.test(own)) {
    fetch(`/api/waitlist/me?code=${own}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((s) => {
        fill(s)
        identify(s.code, { position: s.position, tier: s.tier?.name ?? null, referrals: s.referrals ?? 0 })
        // Opened their own link: they want their spot.
        if ((new URLSearchParams(location.search).get("w") || "").toUpperCase() === own) openSpot()
      })
      .catch((status) => status === 404 && store.set(OWN, null))
  }
  // Once on the list, the nav button shows your spot instead of the form.
  document.querySelectorAll("[data-nav-join]").forEach((a) =>
    a.addEventListener("click", (e) => {
      if (!current) return
      e.preventDefault()
      e.stopImmediatePropagation()
      openSpot()
    }),
  )

  // A friend's link: say so, gently, at the bottom.
  const banner = document.querySelector("[data-friend-banner]")
  if (banner && friendCode() && !store.get(OWN)) {
    banner.hidden = false
    track("friend_banner_shown")
    if (motionOK()) animate(banner, { opacity: [0, 1], y: [30, 0] }, { type: "spring", bounce: 0, duration: 0.6, delay: 0.8 })
    banner.querySelector("[data-friend-dismiss]").addEventListener("click", () => (banner.hidden = true))
    banner.querySelector('a[href="#waitlist"]').addEventListener("click", () => (banner.hidden = true))
  }
}

// ── /spot/CODE: the card on a page of its own, to bookmark ──
const inline = document.querySelector("[data-spot-inline]")
if (inline && card) {
  const code = (inline.dataset.spotInline || "").toUpperCase()
  inline.append(card)
  $("[data-spot-close]").hidden = true
  fetch(`/api/waitlist/me?code=${code}`)
    .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
    .then((s) => {
      store.set(OWN, s.code)
      fill(s)
      identify(s.code, { position: s.position, tier: s.tier?.name ?? null, referrals: s.referrals ?? 0 })
      track("spot_page_viewed", { position: s.position })
      if (motionOK()) animate($("#spot-title"), { scale: [0.8, 1], opacity: [0, 1] }, { type: "spring", bounce: 0.35, duration: 0.6 })
    })
    .catch(() => {
      inline.innerHTML = '<p class="p-8 text-center text-lg">We couldn\'t find that spot. <a class="underline" href="/#waitlist">Join the waitlist</a></p>'
    })
}
